// src/services/warehouse-locations.service.ts
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

export interface Warehouse {
  id: string;
  name: string;
  code: string | null;
  isActive: boolean;
  organizationId: string;
  createdAt: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
}

export type MovementStage = "In Transit" | "Staged" | "On Site";

export const MOVEMENT_STAGES: MovementStage[] = ["In Transit", "Staged", "On Site"];

function mapWarehouse(row: Record<string, unknown>): Warehouse {
  return {
    id: String(row.id),
    name: String(row.name),
    code: row.code ? String(row.code) : null,
    isActive: Boolean(row.is_active ?? true),
    organizationId: String(row.organization_id),
    createdAt: String(row.created_at),
    address: (row.address as string) ?? null,
    latitude: row.latitude != null ? Number(row.latitude) : null,
    longitude: row.longitude != null ? Number(row.longitude) : null,
  };
}

export const WarehouseLocationsService = {
  async list(): Promise<Warehouse[]> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return [];

    const { data, error } = await supabase
      .from("warehouses")
      .select("*")
      .eq("organization_id", orgId)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (error) {
      console.error("[WarehouseLocationsService] list error:", error);
      return [];
    }

    return (data ?? []).map((row) => mapWarehouse(row as Record<string, unknown>));
  },

  async create(
    name: string,
    code?: string,
    address?: string,
    latitude?: number | null,
    longitude?: number | null,
  ): Promise<Warehouse | null> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return null;

    const { data, error } = await supabase
      .from("warehouses")
      .insert({
        name: name.trim(),
        code: code?.trim() || null,
        organization_id: orgId,
        address: address?.trim() || null,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error("[WarehouseLocationsService] create error:", error);
      return null;
    }

    return mapWarehouse(data as Record<string, unknown>);
  },

  async update(
    id: string,
    name: string,
    code?: string,
    address?: string,
    latitude?: number | null,
    longitude?: number | null,
  ): Promise<Warehouse | null> {
    const { data, error } = await supabase
      .from("warehouses")
      .update({
        name: name.trim(),
        code: code?.trim() || null,
        address: address?.trim() || null,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[WarehouseLocationsService] update error:", error);
      return null;
    }

    return mapWarehouse(data as Record<string, unknown>);
  },

  async deactivate(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("warehouses")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[WarehouseLocationsService] deactivate error:", error);
      return false;
    }

    return true;
  },

};
